/**
 * Quick chat: a short question from the home screen, answered in place by an
 * assistant without a persona. Keeps the latest answers locally so they can be
 * reopened from the home screen.
 *
 * The transport is swappable. Today it rides the regular session chat
 * (/voice_chat/start + /chat) with session_type "quick_chat", because that is
 * the only backend path that executes the user's cloud tools. The backend
 * writes a persona-less framing prompt for "quick_chat" and puts it first;
 * the client adds only its own context after it (see buildContextMessage).
 * When a lean persona-less endpoint exists, add a transport for it and switch
 * DEFAULT_TRANSPORT — nothing else changes.
 */
import { startChat, stopChat, sendMessage, getUserProfile } from './api'

export interface QuickChatEntry {
  id: string
  question: string
  answer: string
  status: 'pending' | 'done' | 'error'
  createdAt: string
  /** Tool progress labels seen while answering, e.g. "Searching the web..." */
  tools: string[]
  /** A tool created or changed the user's data — home cards should refresh */
  changedData?: boolean
  error?: string
}

export const QUICK_CHAT_LIMIT = 10
const STORAGE_KEY = 'pipar_quick_chats'

export function loadQuickChats(): QuickChatEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const parsed = raw ? (JSON.parse(raw) as QuickChatEntry[]) : []
    if (!Array.isArray(parsed)) return []
    // An entry still pending after a reload was interrupted mid-stream.
    return parsed.slice(0, QUICK_CHAT_LIMIT).map((e) =>
      e.status === 'pending' ? { ...e, status: 'error' as const, error: e.error || 'Vastaus keskeytyi' } : e,
    )
  } catch {
    return []
  }
}

function saveQuickChats(entries: QuickChatEntry[]): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(0, QUICK_CHAT_LIMIT))) } catch { /* storage unavailable */ }
}

// ── Transports ──────────────────────────────────────────────────

export interface QuickChatHandlers {
  onText: (delta: string) => void
  onTool: (status: string) => void
  onDataChanged: () => void
}

export interface QuickChatTransport {
  name: string
  ask: (question: string, handlers: QuickChatHandlers) => Promise<void>
}

/** Owner of anything the tools create. The backend uses it for tool ownership only — a
 *  quick_chat session gets no persona prompt. */
const TOOL_OWNER_PERSONA = 'pipar'

/** Cheap, capable tier for one-off answers (DeepSeek V4-Flash); allowed on every tier. */
const QUICK_CHAT_MODEL = 'pipar-basic'

/**
 * Client context sent after the server's framing prompt — the same "ABOUT THE
 * USER" block the mobile app builds (QuickChatService._buildContextMessage,
 * UserInformation.toSystemPromptString), so "what should I call you"-type
 * questions work. Occupation isn't synced to the cloud profile, so only name and
 * details. No location: the web client has no standing access to it.
 * Best-effort — a failed profile fetch just sends no context.
 */
async function buildContextMessage(): Promise<string> {
  try {
    const profile = await getUserProfile()
    const parts: string[] = []
    if (profile.user_name.trim()) parts.push(`Name: ${profile.user_name}`)
    if (profile.additional_details.trim()) parts.push(`Additional Details: ${profile.additional_details}`)
    return parts.length ? `ABOUT THE USER:\n${parts.join('\n')}` : ''
  } catch (e) {
    console.warn('[quickChat] user profile unavailable', e)
    return ''
  }
}

/** One throwaway session per question: start → one /chat turn → stop. */
export const sessionTransport: QuickChatTransport = {
  name: 'session chat (quick_chat)',
  async ask(question, h) {
    const context = await buildContextMessage()
    const started = await startChat({
      personaId: TOOL_OWNER_PERSONA,
      sessionType: 'quick_chat',
      model: QUICK_CHAT_MODEL,
      // Quick questions leave nothing behind: no cloud session, no messages.
      saveToCloud: false,
      messages: context ? [{ role: 'system', content: context }] : [],
    })
    const sessionId = started.voice_chat_id
    let gotText = false
    let stopped = false
    try {
      for await (const event of sendMessage(sessionId, question, QUICK_CHAT_MODEL)) {
        if ((event.type === 'text' || event.type === 'content') && typeof event.data === 'string' && event.data) gotText = true
        switch (event.type) {
          case 'text':
          case 'content':
            if (typeof event.data === 'string') h.onText(event.data)
            break
          case 'tool_progress': {
            const data = event.data as Record<string, unknown> | undefined
            if (data?.status) h.onTool(String(data.status))
            break
          }
          case 'cloud_tool_result':
            h.onDataChanged()
            break
          case 'guard_active':
          case 'model_access':
            throw new Error(event.error || 'Mallia ei voi käyttää juuri nyt')
          case 'error':
            throw new Error(typeof event.data === 'string' ? event.data : 'Virhe vastauksessa')
        }
      }
      // Stop before returning. If the stream carried no text, recover the
      // answer from the session history the stop call returns.
      stopped = true
      const stop = await stopChat(sessionId, TOOL_OWNER_PERSONA).catch((e) => {
        console.warn('[quickChat] stop failed', e)
        return null
      })
      if (!gotText) {
        const last = [...(stop?.messages || [])].reverse().find((m) => m.role === 'assistant' && m.content)
        if (last) h.onText(last.content)
      }
    } finally {
      if (!stopped) stopChat(sessionId, TOOL_OWNER_PERSONA).catch((e) => console.warn('[quickChat] stop failed', e))
    }
  },
}

const DEFAULT_TRANSPORT = sessionTransport

// ── Ask ─────────────────────────────────────────────────────────

/**
 * Ask a question and stream the answer into the stored list. `onChange` gets
 * the whole list (newest first) on every update; the first call happens
 * synchronously so the caller can show the pending entry immediately.
 */
export async function askQuickChat(
  question: string,
  onChange: (entries: QuickChatEntry[]) => void,
  transport: QuickChatTransport = DEFAULT_TRANSPORT,
): Promise<QuickChatEntry> {
  let entry: QuickChatEntry = {
    id: crypto.randomUUID(),
    question,
    answer: '',
    status: 'pending',
    createdAt: new Date().toISOString(),
    tools: [],
  }
  let entries = loadQuickChats()

  const commit = (next: QuickChatEntry) => {
    entry = next
    entries = [next, ...entries.filter((e) => e.id !== next.id)].slice(0, QUICK_CHAT_LIMIT)
    saveQuickChats(entries)
    onChange(entries)
  }

  commit(entry)
  try {
    await transport.ask(question, {
      onText: (delta) => commit({ ...entry, answer: entry.answer + delta }),
      onTool: (status) => {
        if (!entry.tools.includes(status)) commit({ ...entry, tools: [...entry.tools, status] })
      },
      onDataChanged: () => { if (!entry.changedData) commit({ ...entry, changedData: true }) },
    })
    commit(entry.answer.trim()
      ? { ...entry, status: 'done' }
      : { ...entry, status: 'error', error: 'Tyhjä vastaus' })
  } catch (e) {
    console.warn(`[quickChat] ${transport.name} failed`, e)
    commit({ ...entry, status: 'error', error: e instanceof Error ? e.message : String(e) })
  }
  return entry
}
